import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    View,
    Drawer,
    Button,
    CheckboxFilter,
    PopupFilter,
    Heading,
    Icon,
    colors,
    Link,
    BasicChip,
} from '@fairygodboss/ui';

export const MobileFilter = ({
    remoteSelected,
    onRemoteSelect,
    companies,
    locations,
    activeCompanies,
    onClearFilters,
    activeLocations,
    onCompanySelect,
    onLocationSelect,
    isAuthenticatedUser,
    hasRemote,
}) => {
    const bodyRef = useRef(document.querySelector('body'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [companiesOpen, isCompaniesOpen] = useState(false);
    const [locationsOpen, isLocationsOpen] = useState(false);
    const [selectedCompanies, setSelectedCompanies] = useState(activeCompanies);
    const [selectedLocations, setSelectedLocations] = useState(activeLocations);
    const [remoteChecked, setIsRemoteChecked] = useState(remoteSelected);
    const [activeFilters, setActiveFilters] = useState(0);
    const drawerRef = useRef();
    const historyRef = useRef(window.history.state);
    historyRef.current = window.history.state;

    useEffect(() => {
        drawerRef.current = drawerOpen;
        if (!drawerOpen) {
            isCompaniesOpen(false);
            isLocationsOpen(false);
        }
    }, [drawerOpen]);

    useEffect(() => {
        if (drawerOpen) {
            window.history.pushState(
                { mainDrawerOpen: true },
                '',
                window.location.href
            );
        }
    }, [drawerOpen]);

    const manageState = useCallback(() => {
        const _stateHistory = historyRef.current;
        if (_stateHistory.mainDrawerOpen) {
            if (drawerOpen && !companiesOpen && !locationsOpen) {
                window.history.pushState({}, '', window.location.href);
                setDrawerOpen(false);
                return;
            }
            window.history.pushState({}, '', window.location.href);

            setDrawerOpen(true);
            isCompaniesOpen(false);
            isLocationsOpen(false);
        } else if (drawerRef.current) {
            setDrawerOpen(false);
            window.history.replaceState({}, '', window.location.href);
        }
    }, [companiesOpen, drawerOpen, locationsOpen]);
    // handle pushState pop for mobile jobs card
    useEffect(() => {
        // initiate the event handler
        window.addEventListener('popstate', manageState);

        // this will clean up the event every time the component is re-rendered
        return function cleanup() {
            window.removeEventListener('popstate', manageState);
        };
    }, [manageState]);

    useEffect(() => {
        setIsRemoteChecked(remoteSelected);
    }, [remoteSelected]);

    useEffect(() => {
        setSelectedCompanies(activeCompanies);
    }, [activeCompanies]);

    useEffect(() => {
        setSelectedLocations(activeLocations);
    }, [activeLocations]);

    useEffect(() => {
        const _remote = remoteChecked ? 1 : 0;
        const _companies = selectedCompanies?.length || 0;
        const _locations = selectedLocations?.length || 0;
        const _total = _remote + _companies + _locations;
        setActiveFilters(_total);
    }, [selectedCompanies, selectedLocations, remoteChecked]);

    // Prevent body from scrolling when modal is open
    const [preventRootScrolling, setPreventRootScrolling] = useState(false);

    useEffect(() => {
        if ((companiesOpen || locationsOpen) && !preventRootScrolling) {
            setPreventRootScrolling(true);
            return;
        }

        if (preventRootScrolling && !(companiesOpen || locationsOpen)) {
            setPreventRootScrolling(false);
        }
    }, [companiesOpen, locationsOpen, preventRootScrolling]);

    useEffect(() => {
        const root = document.getElementById('root');
        if (preventRootScrolling && root) {
            root.style.overflow = 'hidden';
        }
        if (!preventRootScrolling && root) {
            root?.style?.removeProperty('overflow');
        }
        return () => root?.style?.removeProperty('overflow');
    }, [preventRootScrolling]);

    return (
        <View
            p={isAuthenticatedUser ? '16px' : '24px'}
            display="flex"
            justifyContent="space-between"
        >
            <Drawer
                id="mobile-jobsearch-filter"
                useFocusTrap={false}
                isOpen={drawerOpen}
                selector={bodyRef}
                fullWidth
                position="bottom"
                marginTop="56px"
                css={{
                    '*': {
                        WebkitTapHighlightColor: 'transparent',
                        outline: 'none',
                        fontFamily: "'Open Sans', sans-serif",
                    },
                }}
            >
                <>
                    <View display="grid">
                        <View
                            p="16px"
                            display=" flex"
                            justifyContent="space-between"
                            alignItems="center"
                            borderBottom="solid 1px #C3C3C3"
                        >
                            <Heading
                                fontFamily="'Lato', sans-serif"
                                variant="headingXs"
                            >
                                Search filters
                            </Heading>
                            <Button
                                size="medium"
                                variant="secondaryText"
                                onPress={(e) => {
                                    e?.preventDefault();
                                    setSelectedCompanies(activeCompanies);
                                    setSelectedLocations(activeLocations);
                                    setIsRemoteChecked(remoteSelected);
                                    setDrawerOpen(false);
                                }}
                            >
                                <Icon variant="close_20" />
                            </Button>
                        </View>
                        <View p="16px" display="grid">
                            {hasRemote ? (
                                <View
                                    py="24px"
                                    css={{
                                        button: {
                                            width: '100%',
                                        },
                                    }}
                                >
                                    <CheckboxFilter
                                        checked={remoteChecked}
                                        onClick={() => {
                                            setIsRemoteChecked(!remoteChecked);
                                        }}
                                        label="Remote/Flexible"
                                    />
                                </View>
                            ) : null}
                            <PopupFilter
                                open={companiesOpen}
                                mx="24px"
                                label="Companies"
                                labelName="Companies"
                                isMulti
                                options={companies?.map((c) => ({
                                    label: c.displayName,
                                    value: c.value,
                                    count: c.count,
                                }))}
                                onFilterPress={() => {
                                    isCompaniesOpen(!companiesOpen);
                                }}
                                selectionChange={(val) => {
                                    setSelectedCompanies(val);
                                }}
                                onPress={() => null}
                                selector={bodyRef}
                                selection={selectedCompanies}
                                css={{
                                    margin: '0px',
                                    width: '100%',
                                    marginBottom: '24px',
                                    marginTop: !hasRemote ? '24px' : '0px',
                                }}
                            />
                            <div
                                style={{ visibility: 'hidden', width: '-27px' }}
                            >
                                <PopupFilter
                                    open={locationsOpen}
                                    mx="24px"
                                    label="Locations"
                                    labelName="Locations"
                                    isMulti
                                    options={locations?.map((c) => ({
                                        label: c.displayName,
                                        value: c.value,
                                        count: c.count,
                                    }))}
                                    onFilterPress={() => {
                                        isLocationsOpen(!locationsOpen);
                                    }}
                                    selectionChange={(val) => {
                                        setSelectedLocations(val);
                                    }}
                                    onPress={() => null}
                                    selector={bodyRef}
                                    selection={selectedLocations}
                                    css={{ margin: '0px', width: '100%' }}
                                />
                            </div>
                        </View>
                    </View>
                    <View
                        display="flex"
                        justifyContent="space-between"
                        position="absolute"
                        bottom="0px"
                        width="100%"
                        p="16px"
                        borderTop="solid 1px #C3C3C3"
                    >
                        <Button
                            size="large"
                            variant="secondary"
                            onPress={(e) => {
                                e?.preventDefault();
                                setSelectedCompanies([]);
                                setSelectedLocations([]);
                                setIsRemoteChecked(false);
                                setDrawerOpen(false);
                                setTimeout(() => onClearFilters(), 300);
                            }}
                        >
                            Clear filters
                        </Button>
                        <Button
                            size="large"
                            onPress={() => {
                                setDrawerOpen(false);
                                setTimeout(() => {
                                    onCompanySelect(selectedCompanies);
                                    onLocationSelect(selectedLocations);
                                    if (remoteChecked !== remoteSelected) {
                                        onRemoteSelect();
                                    }
                                }, 300);
                            }}
                        >
                            Apply filters
                        </Button>
                    </View>
                </>
            </Drawer>
            {isAuthenticatedUser ? (
                <Button
                    leftIcon={
                        <Icon
                            variant="settings_slider_16"
                            outlineColor={colors.secondary[80]}
                        />
                    }
                    onPress={(e) => {
                        e?.preventDefault();
                        setDrawerOpen(true);
                    }}
                    size="medium"
                    variant="secondary"
                >
                    Edit filters
                    {activeFilters ? (
                        <BasicChip
                            ml="8px"
                            inverted
                            text={activeFilters}
                            css={{
                                minWidth: '16px',
                                height: '20px',
                                width: '20px',
                                alignItems: 'center',
                                fontSize: '13px',
                            }}
                        />
                    ) : null}
                </Button>
            ) : (
                <Button
                    width="100%"
                    onPress={(e) => {
                        e?.preventDefault();
                        setDrawerOpen(true);
                    }}
                    size="medium"
                    variant="secondary"
                >
                    <View display="flex" justifyContent="center">
                        <Icon
                            pr="8px"
                            variant="settings_slider_16"
                            outlineColor={colors.secondary[80]}
                        />
                        Edit filters
                        {activeFilters ? (
                            <BasicChip
                                ml="8px"
                                inverted
                                text={activeFilters}
                                css={{
                                    minWidth: '16px',
                                    height: '20px',
                                    width: '20px',
                                    alignItems: 'center',
                                    fontSize: '13px',
                                }}
                            />
                        ) : null}
                    </View>
                </Button>
            )}
            {isAuthenticatedUser ? (
                <View
                    css={{
                        'a:visited': {
                            color: `${colors.primary[60]} !important`,
                            'div > svg': {
                                path: {
                                    fill: colors.primary[60],
                                },
                            },
                            'a:hover': {
                                color: `${colors.primary[80]} !important`,
                                'div > svg': {
                                    path: {
                                        fill: colors.primary[80],
                                    },
                                },
                            },
                        },
                    }}
                >
                    <Link href="/jobs/saved" icon variant="standalone">
                        Saved Jobs
                    </Link>
                </View>
            ) : null}
        </View>
    );
};
